// The Plan room's reads over the project, pure (no DOM, no React) so the unit tests run them in Node: collections as
// the read-only flat arrangements the Mac caches (`ProjectSnapshot.searchResults`), heat and cold by node for the
// corkboard and the contents (`RevisionHeat.byNode`, `isCold`), freeform points as stored, the beat board's scenes,
// alternates at a node, and the outliner's sorting and grouping (OutlinerView's `sortOrder` and `Grouping`).
import type * as A from '@automerge/automerge';
import type { ArrangementInfo, Field, FragmentKind, MetadataValue, OpenedProject, TreeNode } from '@writing-suite/core';
import { alternateSets, effectiveLocks, fragmentTags, tagCounts, type Project, type TagCount } from '@writing-suite/core';
import {
  beatScenes, beatSheetOf, placedPoints, savedSearchesOf, sceneKindsOf, searchMembers, workflowRank,
  type BeatScene, type BeatSheet, type BoardPoint, type SavedSearch,
} from '@writing-suite/core/plan';
import { isCold } from '@writing-suite/core/revise';

type AlternateSet = ReturnType<typeof alternateSets>[number];

// MARK: Collections

/** The project's collections as the picker lists them (by name). */
export function collectionInfos(project: Pick<OpenedProject, 'record'>): ArrangementInfo[] {
  return savedSearchesOf(project.record.settings).map((s) => ({ id: s.id, name: s.name, role: 'collection' as const }));
}

export const collectionOf = (project: Pick<OpenedProject, 'record'>, id: string): SavedSearch | null =>
  savedSearchesOf(project.record.settings).find((s) => s.id === id) ?? null;

/** Every fragment's document, as `searchMembers` reads them. */
function docsOf(project: OpenedProject): Map<string, A.Doc<unknown>> {
  const docs = new Map<string, A.Doc<unknown>>();
  for (const id of project.fragmentIds()) {
    const d = project.fragmentDoc(id);
    if (d) docs.set(`fragment.${id}`, d);
  }
  return docs;
}

const membersCache = new WeakMap<object, Map<string, string[]>>();

/** A search's members in reading order (`DocumentStore.members(of:in:)`), for the project as it is. */
export function membersOf(project: OpenedProject, search: SavedSearch, now: Date = new Date()): string[] {
  const key = JSON.stringify(search);
  let cached = membersCache.get(project);
  if (!cached) membersCache.set(project, (cached = new Map()));
  const hit = cached.get(key);
  if (hit) return hit;
  const members = searchMembers(docsOf(project), project.record, [search], now).get(search.id) ?? [];
  cached.set(key, members);
  return members;
}

/** A collection shown in the contents: one flat item per member, whose node id is the fragment's (`resultsArrangement`). */
export function collectionTree(project: OpenedProject, id: string, now: Date = new Date()): TreeNode[] {
  const search = collectionOf(project, id);
  if (!search) return [];
  return membersOf(project, search, now).map((fid) => ({
    id: fid, fragmentId: fid, title: project.fragmentRecord(fid)?.title ?? 'Untitled', isFolder: false, children: [],
  }));
}

/** `DocumentStore.effectiveLocks`: the documents under lock, themselves or by a folder above them. */
export function locksOf(project: Project): Set<string> {
  const opened = project as OpenedProject;
  if (!opened.record) return new Set();
  const own = new Set(opened.fragmentIds().filter((id) => opened.fragmentRecord(id)?.locked));
  return effectiveLocks(opened.record.arrangements, own);
}

/** The contents' chapter and part numbers by fragment id, as compile numbers them: chapters "14", parts "II" (an
 *  unnumbered one, a prologue, has none). */
export function chapterNumbers(project: Project): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, o] of project.openers()) {
    if (o.number === null) continue;
    out.set(id, o.kind === 'part' ? roman(o.number) : String(o.number));
  }
  return out;
}

function roman(n: number): string {
  const table: [number, string][] = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let out = '';
  for (const [value, glyph] of table) while (n >= value) { out += glyph; n -= value; }
  return out;
}

/** `LibraryModel.projectTags`: the tags in use (documents in the Trash aside) and how many documents have each. */
export function projectTags(project: OpenedProject): TagCount[] {
  return tagCounts(
    project.fragmentIds().flatMap((id) => {
      const record = project.fragmentRecord(id);
      return record && !project.record.fragments.get(id)?.trashed ? [fragmentTags(record)] : [];
    }),
  );
}

// MARK: The contents' rows

/** What a document's row shows of it: its words, its label's colour ("r g b" as stored, or none) and its kind. */
export interface RowFact {
  words: number;
  label: string | null;
  kind: FragmentKind;
  /** In the compile (the fragment's Include in Compile; every document is until unticked). */
  included: boolean;
}
export type RowFacts = ReadonlyMap<string, RowFact>;

/** Every fragment's row facts, by fragment id. */
export function rowFactsOf(project: OpenedProject): Map<string, RowFact> {
  const label = project.record.fields.find((f) => f.id === 'label');
  const out = new Map<string, RowFact>();
  for (const id of project.fragmentIds()) {
    const f = project.fragmentRecord(id);
    if (!f) continue;
    const v = f.metadata.label;
    const option = v?.type === 'text' ? label?.options.find((o) => o.id === v.value) : undefined;
    out.set(id, { words: project.words(id), label: option?.color ?? null, kind: f.kind, included: f.includeInCompile ?? true });
  }
  return out;
}

export function sameRowFacts(a: RowFacts, b: RowFacts): boolean {
  if (a.size !== b.size) return false;
  for (const [id, x] of a) {
    const y = b.get(id);
    if (!y || x.words !== y.words || x.label !== y.label || x.kind !== y.kind || x.included !== y.included) return false;
  }
  return true;
}

// MARK: Heat and cold

/** `RevisionHeat.byNode` over the web's tree: a scene's own level, a folder's the hottest in it. */
export function heatOfNodes(tree: TreeNode[], levels: Readonly<Record<string, number>>): Record<string, number> {
  const out: Record<string, number> = {};
  const walk = (n: TreeNode): number => {
    const own = n.fragmentId ? (levels[n.fragmentId] ?? 0) : 0;
    const level = n.children.reduce((m, c) => Math.max(m, walk(c)), own);
    if (level > 0) out[n.id] = level;
    return level;
  };
  tree.forEach(walk);
  return out;
}

/** A scene (never a folder) neither opened in this browser nor changed anywhere in the last `coldDays` (0: never). */
export function isColdScene(n: TreeNode, modifiedAt: Date | null | undefined, opened: Readonly<Record<string, Date>>, coldDays: number,
  now: Date = new Date()): boolean {
  if (coldDays <= 0 || n.isFolder || n.children.length > 0 || !n.fragmentId) return false;
  return isCold(opened[n.fragmentId], modifiedAt, new Date(now.getTime() - coldDays * 86_400_000));
}

// MARK: The freeform board

/** The cards' stored places in an arrangement (`boardX`/`boardY`), by node id. */
export function storedPoints(project: Pick<OpenedProject, 'record'>, arrangementId: string): Map<string, BoardPoint> {
  const a = project.record.arrangements.find((x) => x.id === arrangementId);
  return a ? placedPoints(a.nodes.values()) : new Map();
}

// MARK: Beats

export function beatSheetFor(project: Pick<OpenedProject, 'record'>): BeatSheet | null {
  return beatSheetOf(project.record.settings);
}

/** The manuscript's scenes on the beat board (`beatScenes`), in reading order. */
export function beatScenesFor(project: OpenedProject, sheet: BeatSheet | null): BeatScene[] {
  const sources = project.scenes(sceneKindsOf(project.type)).map((s) => {
    const f = project.fragment(s.id);
    return { id: s.id, isFolder: s.isFolder, title: f?.title || 'Untitled', synopsis: f?.synopsis ?? '', words: project.words(s.id), metadata: f?.metadata ?? {} };
  });
  return beatScenes(sources, sheet);
}

/** "near 10%" typed as the Mac's percentage field takes it: "25%", "25 %" or a bare 25; empty = none; 0–100. */
export function parseNear(text: string): number | null | undefined {
  const t = text.trim().replace(/\s*%$/, '');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(1, Math.max(0, Math.round(n) / 100));
}

// MARK: Alternates

/** The scene or folder set at a node (with alternates), if any. */
export function alternateSetAt(project: Pick<OpenedProject, 'record'>, arrangementId: string, nodeId: string | null | undefined): AlternateSet | null {
  if (!nodeId) return null;
  return alternateSets(project.record).find((s) => s.kind !== 'arrangement' && s.nodeID === nodeId && s.arrangementID === arrangementId) ?? null;
}

/** The arrangement set an arrangement belongs to (Fork, Make This the Manuscript). */
export function arrangementSetOf(project: Pick<OpenedProject, 'record'>, arrangementId: string): AlternateSet | null {
  return alternateSets(project.record).find((s) => s.kind === 'arrangement' && s.members.some((m) => m.arrangementID === arrangementId)) ?? null;
}

/** An alternate scene open in Write: the fragment given (`?alternate=`) when it's a non-current member of the node's
 *  scene set. */
export function editingAlternate(project: Pick<OpenedProject, 'record'>, arrangementId: string, nodeId: string | null | undefined, fragmentId: string | undefined):
  { set: AlternateSet; member: AlternateSet['members'][number] } | null {
  if (!fragmentId) return null;
  const set = alternateSetAt(project, arrangementId, nodeId);
  if (!set || set.kind !== 'fragment') return null;
  const member = set.members.find((m) => m.fragmentID === fragmentId && m.id !== set.currentMemberID);
  return member ? { set, member } : null;
}

/** "Version 2", "Version 3"…: the next name not taken in a set. */
export function nextVersionName(set: AlternateSet | null): string {
  const taken = new Set(set?.members.map((m) => m.name) ?? []);
  let n = Math.max(2, (set?.members.length ?? 1) + 1);
  while (taken.has(`Version ${n}`)) n++;
  return `Version ${n}`;
}

// MARK: The outliner

export type Grouping = 'none' | 'status' | 'label' | 'pov';
export const GROUPINGS: { id: Grouping; name: string }[] = [
  { id: 'none', name: 'None' }, { id: 'status', name: 'Status' }, { id: 'label', name: 'Label' }, { id: 'pov', name: 'POV' },
];

export interface OutlineRow {
  node: TreeNode;
  depth: number;
}

export interface SortKey {
  /** "title", "words", "synopsis" or "field:<id>". */
  column: string;
  ascending: boolean;
}

/** What a row sorts by in a column: text (compared as Finder compares names) or a number. */
export type SortValue = string | number | null;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Rows sorted by a column (the tree flattened, as the Mac's table flattens it when sorted); empty values last. */
export function sortRows(rows: OutlineRow[], key: SortKey, valueOf: (row: OutlineRow) => SortValue): OutlineRow[] {
  const keyed = rows.map((row, i) => ({ row, i, v: valueOf(row) }));
  keyed.sort((a, b) => {
    if (a.v === null || a.v === '') return b.v === null || b.v === '' ? a.i - b.i : 1;
    if (b.v === null || b.v === '') return -1;
    const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : collator.compare(String(a.v), String(b.v));
    return (key.ascending ? c : -c) || a.i - b.i;
  });
  return keyed.map((k) => ({ node: k.row.node, depth: 0 }));
}

/** Rows grouped by a field (`Grouping`): status in workflow order, label in its options' order, POV by name; the
 *  rows without a value last ("No Status"). Only documents with a fragment are grouped. */
export function groupRows(rows: OutlineRow[], field: Field | undefined, metadataOf: (fragmentId: string) => Record<string, MetadataValue> | undefined,
  nameOf: (value: string) => string): { key: string | null; title: string; rows: OutlineRow[] }[] {
  if (!field) return [{ key: null, title: '', rows }];
  const groups = new Map<string | null, OutlineRow[]>();
  for (const row of rows) {
    if (!row.node.fragmentId) continue;
    const v = metadataOf(row.node.fragmentId)?.[field.id];
    const key = v?.type === 'text' && v.value !== '' ? v.value : null;
    groups.set(key, [...(groups.get(key) ?? []), { node: row.node, depth: 0 }]);
  }
  const keys = [...groups.keys()].filter((k): k is string => k !== null);
  keys.sort((a, b) => {
    if (field.type === 'enum') return workflowRank(field, a) - workflowRank(field, b) || collator.compare(nameOf(a), nameOf(b));
    return collator.compare(nameOf(a), nameOf(b));
  });
  const out = keys.map((k) => ({ key: k as string | null, title: nameOf(k), rows: groups.get(k)! }));
  if (groups.has(null)) out.push({ key: null, title: `No ${field.name}`, rows: groups.get(null)! });
  return out;
}
